import { z } from "zod";

import type { ListStaffUsageParams } from "../../api/generated/model";
import { UsageGroup } from "../../api/generated/zod";

/** The groupings, in the order the screen offers them; user is the API's default. */
export const GROUPS: { value: UsageGroup; label: string }[] = [
  { value: "user", label: "User" },
  { value: "team", label: "Team" },
  { value: "brand", label: "Brand" },
  { value: "model", label: "Model" },
  { value: "type", label: "Type" },
  { value: "day", label: "Day" },
];

/** A grouping that filters by its key; a day's key narrows the range instead. */
export type FilterGroup = Exclude<UsageGroup, "day">;

const day = z.iso.date();

// `?from=&to=`: the UTC days a generation started in, the last 30 up to today when left out (as
// the API's default). `?team=&brand=&user=&model=&type=`: the filters. `?by=`: the grouping, user
// when left out. Anything malformed is dropped.
export const usageSearch = z.object({
  from: day.optional().catch(undefined),
  to: day.optional().catch(undefined),
  team: z.uuid().optional().catch(undefined),
  brand: z.uuid().optional().catch(undefined),
  user: z.uuid().optional().catch(undefined),
  model: z.string().min(1).max(200).optional().catch(undefined),
  type: z.string().min(1).max(200).optional().catch(undefined),
  by: UsageGroup.optional().catch(undefined),
});
export type UsageSearch = z.infer<typeof usageSearch>;

// The generations behind one row of Usage: Usage's own search, the row's `?key=` under its
// grouping (a user's id, a day), and `?steps=` to show each run's steps under it.
export const generationsSearch = usageSearch.extend({
  key: z.string().min(1).max(200).optional().catch(undefined),
  steps: z.boolean().optional().catch(undefined),
});

/** The days a range is when left out: the last 30, as the API's default. */
export const DEFAULT_DAYS = 30;

/** Today as the API counts days: in UTC. */
export const today = () => new Date().toISOString().slice(0, 10);

/** The UTC day `days` before `day`. */
export function daysBefore(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - days);
  return at.toISOString().slice(0, 10);
}

/** The days a search covers: its own, else the last 30 up to today, as the API reads it. */
export function rangeOf(search: UsageSearch): { from: string; to: string } {
  const to = search.to ?? today();
  return { from: search.from ?? daysBefore(to, DEFAULT_DAYS - 1), to };
}

/**
 * The API's filters for a search. The days are always sent, so the charts draw exactly the days
 * the sums cover.
 */
export function filtersOf(search: UsageSearch): ListStaffUsageParams {
  const { team, brand, user, model, type } = search;
  return {
    ...rangeOf(search),
    ...(team === undefined ? {} : { team_id: team }),
    ...(brand === undefined ? {} : { brand_id: brand }),
    ...(user === undefined ? {} : { user_id: user }),
    ...(model === undefined ? {} : { model_id: model }),
    ...(type === undefined ? {} : { type }),
  };
}

/** A search narrowed to one row's key: the filters its generations are read with. */
export function narrowed(search: UsageSearch, by: UsageGroup, key: string): UsageSearch {
  switch (by) {
    case "day":
      return { ...search, from: key, to: key };
    case "user":
      return { ...search, user: key };
    case "team":
      return { ...search, team: key };
    case "brand":
      return { ...search, brand: key };
    case "model":
      return { ...search, model: key };
    case "type":
      return { ...search, type: key };
  }
}

/** A search without one grouping's filter: what that filter's choices are read with. */
export function without(search: UsageSearch, by: FilterGroup): UsageSearch {
  const rest = { ...search };
  delete rest[by];
  return rest;
}
